import { HiChevronLeft } from 'react-icons/hi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import { BsFiletypeCsv } from 'react-icons/bs';
import { formatDateWithTimezoneOffset } from '../../../../../helpers/utils';
import { SUB_REPORT_QUERY_KEY } from '../../../helpers/querySchema';
import { formatHeaderTitle } from '../../inventory/utils';

interface IHeaderSectionProps {
  headerTitle: string;
  exportData: any;
}

const HeaderSection = ({ headerTitle, exportData }: IHeaderSectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboard, widget } = useParams();

  const handleBackNavigation = () => {
    if (dashboard && widget) {
      const nextQueryParams = new URLSearchParams(location.search);
      nextQueryParams.delete(SUB_REPORT_QUERY_KEY);
      const nextQueryString = nextQueryParams.toString();
      navigate(`/${dashboard}/${widget}/report${nextQueryString ? `?${nextQueryString}` : ''}`);
      return;
    }
    navigate('/');
  };

  const handleExportClick = () => {
    const dataToExport = exportData?.data || [];
    if (!dataToExport || dataToExport.length === 0) return;

    const transformedExportData = dataToExport.map((row: any) => {
      const { staff_id, order_id, offset, timezone, order_date, total_records, rownum, ...rest } =
        row;

      return {
        sl_no: rownum,
        order_date: order_date
          ? formatDateWithTimezoneOffset(order_date, offset, 'MMM dd, yyyy')
          : '',
        ...rest
      };
    });

    const headers = Object.keys(transformedExportData[0]);
    const formattedHeaders = headers.map((header) => formatHeaderTitle(header));
    const rows = transformedExportData.map((row: any) =>
      headers.map((field) => JSON.stringify(row[field] ?? '')).join(',')
    );
    const csvContent = [formattedHeaders.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Sapaad ${headerTitle}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Wrapper>
      <div className="back-navigation-container">
        <HiChevronLeft className="back-navigation" onClick={handleBackNavigation} />
        <span className="subr-header-title">{headerTitle}</span>
      </div>
      <div className="export-button-container">
        {exportData?.data && exportData.data.length > 0 ? (
          <Button variant="primary" size="sm" className="export-button" onClick={handleExportClick}>
            <BsFiletypeCsv size={20} />
            Export
          </Button>
        ) : null}
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin: 40px 0 20px -10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .back-navigation-container {
    display: flex;
    align-items: center;
  }

  .back-navigation {
    cursor: pointer;
    position: relative;
    top: -2px;
    font-size: 40px;
  }

  .subr-header-title {
    font-size: 24px;
    font-weight: 600;
  }

  .export-button {
    background: ${({ theme }) => theme.dropdown.background_color};
    color: ${({ theme }) => theme.text};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border_color};
    cursor: pointer;
    padding: 8px 12px;
    margin: 0;
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease-in-out;

    &.show {
      color: #fff;
    }

    &:hover {
      background: ${({ theme }) => theme.button_plain.hover};
      color: ${({ theme }) => theme.text};
    }

    &:focus {
      outline: 2px solid ${({ theme }) => theme.primary_color};
      outline-offset: 2px;
    }

    &:active {
      outline: 2px solid ${({ theme }) => theme.primary_color};
      border: 1px solid ${({ theme }) => theme.primary_color};
      outline-offset: 2px;
    }

    span {
      margin-left: 8px;
      margin-right: auto;
    }
  }
`;

export default HeaderSection;
